import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchStoreNotesThunk,
  createStoreNoteThunk,
  updateStoreNoteThunk,
  deleteStoreNoteThunk
} from '../features/stores/storeThunks';

const StoreNotesManager = ({ storeId }) => {
  const dispatch = useDispatch();
  const { notesData } = useSelector(state => state.stores);
  const [newNote, setNewNote] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editImportant, setEditImportant] = useState(false);

  useEffect(() => {
    if (storeId) {
      dispatch(fetchStoreNotesThunk(storeId));
    }
  }, [dispatch, storeId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    const noteData = {
      store: storeId,
      note_text: newNote.trim(),
      is_important: isImportant
    };

    const result = await dispatch(createStoreNoteThunk(noteData));
    
    if (createStoreNoteThunk.fulfilled.match(result)) {
      setNewNote('');
      setIsImportant(false);
      // Refresh notes list
      dispatch(fetchStoreNotesThunk(storeId));
    }
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note.id);
    setEditText(note.note_text);
    setEditImportant(note.is_important);
  };

  const handleSaveEdit = async (noteId) => {
    if (!editText.trim()) return;

    const updates = {
      note_text: editText.trim(),
      is_important: editImportant
    };

    const result = await dispatch(updateStoreNoteThunk({ noteId, updates }));
    
    if (updateStoreNoteThunk.fulfilled.match(result)) {
      setEditingNoteId(null);
      setEditText('');
      setEditImportant(false);
      dispatch(fetchStoreNotesThunk(storeId));
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditText('');
    setEditImportant(false);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    const result = await dispatch(deleteStoreNoteThunk(noteId));
    
    if (deleteStoreNoteThunk.fulfilled.match(result)) {
      dispatch(fetchStoreNotesThunk(storeId));
    }
  };

  const notes = notesData.selectedStoreNotes?.notes || [];

  return (
    <div className="store-notes-manager">
      {/* Add New Note */}
      <div className="store-note-input-section">
        <label htmlFor="new-note">Add New Note</label>
        <textarea
          id="new-note"
          className="incident-report-textarea"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Enter note content..."
          rows="3"
        />
        
                <div className="store-note-options">
          <label className="incident-report-radio-option">
            <input
              type="checkbox"
              checked={isImportant}
              onChange={(e) => setIsImportant(e.target.checked)}
            />
            <div className="incident-report-radio-content">
              <div className="incident-report-radio-check"></div>
              <span className="incident-report-radio-title">Mark as important</span>
            </div>
          </label>
          
          <button
            type="button"
            className="incident-report-btn-primary"
            onClick={handleAddNote}
            disabled={!newNote.trim() || notesData.loading}
          >
            Add Note
          </button>
        </div>
      </div>

      {/* Notes List */}
      {notes.length > 0 && (
        <div className="store-notes-list">
          <h4>Existing Notes ({notes.length})</h4>
          {notes.map((note) => (
            <div 
              key={note.id} 
              className={`store-note-item ${note.is_important ? 'important' : ''}`}
            >
              {editingNoteId === note.id ? (
                // Edit Mode
                <div className="store-note-edit-mode">
                  <textarea
                    className="incident-report-textarea"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows="3"
                  />
                  <div className="store-note-edit-options">
                    <label className="incident-report-checkbox-label">
                      <input
                        type="checkbox"
                        checked={editImportant}
                        onChange={(e) => setEditImportant(e.target.checked)}
                      />
                      <span className="incident-report-checkbox"></span>
                      Mark as important
                    </label>
                    <div className="store-note-edit-actions">
                      <button
                        type="button"
                        className="store-note-btn-save"
                        onClick={() => handleSaveEdit(note.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="store-note-btn-cancel"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div className="store-note-content">
                    {note.is_important && (
                      <span className="store-note-important-badge">Important</span>
                    )}
                    <p className="store-note-text">{note.note_text}</p>
                    <div className="store-note-meta">
                      <span>Added by {note.created_by_name}</span>
                      <span>•</span>
                      <span>{new Date(note.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="store-note-actions">
                    <button
                      type="button"
                      className="store-note-btn-edit"
                      onClick={() => handleStartEdit(note)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="store-note-btn-delete"
                      onClick={() => handleDeleteNote(note.id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {notes.length === 0 && (
        <div className="store-notes-empty">
          <p>No notes added yet. Add a note above to get started.</p>
        </div>
      )}

      {notesData.error && (
        <div className="incident-report-error-message">
          {notesData.error}
        </div>
      )}
    </div>
  );
};

export default StoreNotesManager;